import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  SvgIcon,
  Typography,
  alpha,
} from "@mui/material";
import {
  SelfTestBasicData,
  TaskPatientData,
  TaskState,
  TestInterpretation,
  TestResultSource,
  useLazyGetSelfTestDetailQuery,
  useSetImageReviewerInterpretationMutation,
} from "../features/data/patientApiSlice";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useCallback, useEffect, useState } from "react";

import ConcernPillList from "./ConcernPillList";
import EntryContainer from "./EntryContainer";
import InterpretedIcon from "@mui/icons-material/CheckCircleOutlined";
import Photo from "./Photo";
import TaskEntry from "./TaskEntry";
import TestResultIcon from "../../assets/event_rdt_result.svg?react";
import { formatDateTime } from "../utils/dates";
import { getErrorText } from "../utils/errors";
import { useTranslation } from "react-i18next";

export const getSelfTestElementId = (uid: string) => `selfTest-${uid}`;

const SelfTestEntry = (props: {
  selfTestData: SelfTestBasicData;
  expandByDefault?: boolean;
  tasks: TaskPatientData[];
  patientUid: string;
  selectedTaskUid?: string;
  onSelectTask?: (taskUid: string) => void;
  onAddTaskNote?: (taskUid: string, content: string) => void;
  onCompleteTask?: (completeData: { actionsTaken: string[]; notes: string }, task: TaskPatientData) => void;
  onDeleteTask?: (task: TaskPatientData) => void;
}) => {
  const {
    selfTestData,
    expandByDefault,
    tasks,
    patientUid,
    selectedTaskUid,
    onSelectTask,
    onAddTaskNote,
    onCompleteTask,
    onDeleteTask,
  } = props;
  const { t } = useTranslation("SelfTestEntry");

  // Component state
  const [reviewerInterpretation, setReviewerInterpretation] = useState<
    TestInterpretation | null | undefined
  >(undefined);
  const [allowUpdate, setAllowUpdate] = useState(false);

  // Queries/mutations
  const [getSelfTestDetail, selfTestDetailResult] =
    useLazyGetSelfTestDetailQuery();
  const [setImageReviewerInterpretation, imageReviewerInterpretationResult] =
    useSetImageReviewerInterpretationMutation();

  // Callbacks
  const handleExpanded = useCallback(
    () => getSelfTestDetail({ uid: selfTestData.uid }, true),
    [getSelfTestDetail, selfTestData.uid]
  );
  const handleInterpretationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const interpretation = (event.target as HTMLInputElement)
        .value as TestInterpretation;
      setReviewerInterpretation(interpretation);
      setAllowUpdate(
        interpretation !==
          selfTestDetailResult.data?.image_reviewer_interpretation
      );
    },
    [selfTestDetailResult.data]
  );
  const handleUpdateClick = useCallback(() => {
    setImageReviewerInterpretation({
      selfTestUid: selfTestData.uid,
      patientUid,
      interpretation: reviewerInterpretation!,
    });
  }, [
    setImageReviewerInterpretation,
    selfTestData.uid,
    patientUid,
    reviewerInterpretation,
  ]);

  // Reactive code
  useEffect(() => {
    setReviewerInterpretation(
      selfTestDetailResult.data?.image_reviewer_interpretation
    );
    setAllowUpdate(imageReviewerInterpretationResult.isLoading);
  }, [selfTestDetailResult.data, imageReviewerInterpretationResult.isLoading]);

  const taskState =
    tasks.length > 0
      ? tasks.find((task) => task.state === TaskState.todo)
        ? TaskState.todo
        : TaskState.complete
      : undefined;
  // Get the CV interpretation from the last photo
  const cvResultPhoto =
    selfTestDetailResult.data?.photos[
      selfTestDetailResult.data.photos.length - 1
    ];
  const activeTaskCount = tasks.filter(
    (task) => task.state === TaskState.todo
  ).length;
  const isSelfReported =
    selfTestDetailResult.data?.test_result_source ===
    TestResultSource.chatbot_manual;
  const hasPhotos = (selfTestDetailResult.data?.photos ?? []).length > 0;
  return (
    <EntryContainer
      id={getSelfTestElementId(selfTestData.uid)}
      containing
      titleIcon={
        <SvgIcon
          fontSize="large"
          inheritViewBox
          color={taskState === TaskState.todo ? "primary" : "action"}
        >
          <TestResultIcon />
        </SvgIcon>
      }
      titleText={t("title")}
      titleTextPost={
        activeTaskCount > 0
          ? t("common:activeTasks", { count: activeTaskCount })
          : undefined
      }
      subtitleText={formatDateTime(new Date(selfTestData.timestamp))}
      extraTitleComponent={<ConcernPillList tasks={tasks} />}
      expandByDefault={expandByDefault}
      onExpanded={handleExpanded}
      taskStateIndicator={taskState}
      isFetching={selfTestDetailResult.isFetching}
      errorText={
        selfTestDetailResult.isError
          ? getErrorText(selfTestDetailResult.error)
          : ""
      }
      bodyComponent={
        <Stack direction="column">
          {selfTestDetailResult.data ? (
            <Stack direction="column">
              {selfTestDetailResult.data.image_reviewer ? (
                <Box sx={styles.detailBackground}>
                  <InterpretedIcon
                    fontSize="small"
                    color="action"
                    sx={{ marginRight: 1 }}
                  />
                  <Typography variant="detailCellTitle">
                    {t("clinicianResultAdded", {
                      name: selfTestDetailResult.data.image_reviewer.name,
                    })}
                  </Typography>
                </Box>
              ) : null}
              <Typography sx={globalStyles.gapTop} variant="detailCellBody">
                {!isSelfReported
                  ? `${selfTestDetailResult.data.rdt_brand} ${selfTestDetailResult.data.rdt_model} (${selfTestDetailResult.data.rdt_condition})`
                  : t(
                      hasPhotos
                        ? "selfReportedHivStatusWithPhotos"
                        : "selfReportedHivStatus",
                      {
                        result: t(
                          "common:interpretation:" +
                            selfTestDetailResult.data
                              .test_administrator_interpretation
                        ).toLocaleLowerCase(),
                      }
                    )}
              </Typography>
              {hasPhotos ? (
                <Stack direction="row">
                  <Box sx={styles.imagesContainer}>
                    <Stack direction="column">
                      {selfTestDetailResult.data.photos?.map((photo) => (
                        <Stack key={photo.url} direction="row" sx={styles.photoRow}>
                          <Photo url={photo.url} label={t("fullTest")} />
                          {photo.read_window_crop_url ? (
                            <Photo
                              url={photo.read_window_crop_url}
                              label={t("resultWindow")}
                            />
                          ) : null}
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="column">
                    <Box sx={styles.resultCellsContainer}>
                      <Stack direction="column">
                        <Typography variant="detailCellTitle">
                          {t("patientResult")}
                        </Typography>
                        <Typography variant="detailCellBody">
                          {t(
                            "common:interpretation:" +
                              selfTestDetailResult.data
                                .test_administrator_interpretation
                          )}
                        </Typography>
                      </Stack>
                      <Stack direction="column">
                        <Typography variant="detailCellTitle">
                          {t("cvResult")}
                        </Typography>
                        <Typography variant="detailCellBody">
                          {t(
                            cvResultPhoto?.cv_interpretation
                              ? "common:interpretation:" +
                                  cvResultPhoto.cv_interpretation
                              : "noResult"
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={styles.resultCellsContainer}>
                      <Stack direction="column">
                        <Typography variant="detailCellTitle">
                          {t(allowUpdate ? "myResult" : "clinicianResult")}
                        </Typography>
                        <FormControl sx={styles.formContainer}>
                          <RadioGroup
                            row
                            value={reviewerInterpretation || ""}
                            onChange={handleInterpretationChange}
                          >
                            {[
                              TestInterpretation.negative,
                              TestInterpretation.positive,
                              TestInterpretation.invalid,
                              TestInterpretation.uninterpretable,
                            ].map((interpretation) => (
                              <FormControlLabel
                                key={interpretation}
                                value={interpretation}
                                sx={styles.labelContainer}
                                control={<Radio sx={styles.radioButton} />}
                                label={t(
                                  "common:interpretation:" + interpretation
                                )}
                                slotProps={{
                                  typography: { variant: "detailCellBody" },
                                }}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                        {allowUpdate ? (
                          <Box sx={globalStyles.gapTop}>
                            <Button
                              variant="outlined"
                              onClick={handleUpdateClick}
                              disabled={
                                imageReviewerInterpretationResult.isLoading
                              }
                            >
                              {imageReviewerInterpretationResult.isLoading ? (
                                <CircularProgress
                                  size={12}
                                  sx={globalStyles.smallGapRight}
                                />
                              ) : null}
                              {t("common:update")}
                            </Button>
                          </Box>
                        ) : null}
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
          {tasks.map((task) => (
            <TaskEntry
              key={task.uid}
              contained
              task={task}
              selected={task.uid === selectedTaskUid}
              expandByDefault={true}
              onSelect={onSelectTask}
              onAddNote={onAddTaskNote}
              onComplete={onCompleteTask}
              onDelete={onDeleteTask}
            />
          ))}
        </Stack>
      }
    />
  );
};

const styles = createStyleSheet({
  resultCellsContainer: {
    m: 2,
    display: "flex",
    flexDirection: "row",
    gap: 4,
  },
  detailBackground: {
    ...globalStyles.gapTop,
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    borderRadius: 1,
    p: 1,
  },
  detailTextContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
    gap: 0.5,
  },
  photoRow: {
    gap: 2,
  },
  imagesContainer: {
    marginTop: 1,
    padding: 2,
    border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
  },
  formContainer: {
    marginTop: 1,
  },
  labelContainer: {
    marginLeft: 0,
  },
  radioButton: {
    p: 0,
    marginRight: 1,
    py: 0.5,
  },
});

export default SelfTestEntry;
